import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check } from "lucide-react";

const SERVICE_META = {
  basic_interior_wash: {
    name: "Basic Exterior Wash",
    blurb: "Quick exterior refresh",
    features: ["Two-bucket hand wash", "Wheels & tyres clean", "Windows & mirrors", "Spray-on shine"],
  },
  basic_interior: {
    name: "Basic Interior Detail",
    blurb: "Deep interior care",
    features: [
      "Everything in Interior Wash",
      "Dashboard & trim dressing",
      "Door jambs & console detail",
      "Floor mats deep clean",
    ],
    featured: true,
  },
  interior_exterior: {
    name: "Interior + Exterior Detail",
    blurb: "Complete inside-out treatment",
    features: [
      "Everything in Interior Detail",
      "Two-bucket hand wash",
      "Wheel & arch decontamination",
      "Tyre dressing & glass seal",
    ],
  },
  caravan_exterior: {
    name: "Exterior Detail",
    blurb: "Outside wash & finish",
    features: [
      "Hand wash whole exterior",
      "Decontamination & bug removal",
      "Wheels, arches & awnings outside",
      "Windows & finish dressing",
    ],
  },
  caravan_interior: {
    name: "Interior Detail",
    blurb: "Full interior reset",
    features: [
      "Full vacuum (bedding area, floors)",
      "Kitchenette, surfaces & seating",
      "Upholstery & soft furnishing care",
      "Glass, mirrors & floor finish",
    ],
  },
  caravan_full: {
    name: "Interior + Exterior",
    blurb: "Complete inside-out",
    features: [
      "Everything in Interior Detail",
      "Everything in Exterior Detail",
      "Awning & roof attention",
      "Ready-to-roll handover",
    ],
    featured: true,
  },
};

export const VEHICLES = [
  {
    id: "sedan",
    label: "Sedan / Coupe / Hatchback",
    services: {
      basic_interior_wash: 60,
      basic_interior: 70,
      interior_exterior: 125,
    },
  },
  {
    id: "suv",
    label: "SUV / Ute",
    services: {
      basic_interior_wash: 75,
      basic_interior: 85,
      interior_exterior: 145,
    },
  },
  {
    id: "truck",
    label: "Truck / Van",
    services: {
      basic_interior_wash: 90,
      basic_interior: 100,
      interior_exterior: 165,
    },
  },
  {
    id: "caravan",
    label: "Caravan / Campervan",
    services: {
      caravan_exterior: 265,
      caravan_interior: 315,
      caravan_full: 465,
    },
  },
];

export { SERVICE_META };

export default function Services() {
  const [size, setSize] = useState("sedan");

  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gold">
              01 — Services
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-2xl leading-[1.02]">
              Choose your vehicle, pick your detail.
            </h2>
          </div>
          <p className="text-neutral-400 max-w-md">
            Transparent pricing across four vehicle classes — including caravans
            and campervans. Every detail handled in-person.
          </p>
        </div>

        <Tabs value={size} onValueChange={setSize} className="w-full" data-testid="vehicle-size-tabs">
          <TabsList className="bg-[#0a0a0a] border border-white/10 p-1 rounded-full h-auto flex-wrap">
            {VEHICLES.map((v) => (
              <TabsTrigger
                key={v.id}
                value={v.id}
                data-testid={`tab-${v.id}`}
                className="rounded-full px-5 py-2 text-xs uppercase tracking-[0.16em] data-[state=active]:bg-white data-[state=active]:text-black text-neutral-400"
              >
                {v.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {VEHICLES.map((v) => (
            <TabsContent key={v.id} value={v.id} className="mt-10">
              <div className="grid md:grid-cols-3 gap-5">
                {Object.entries(v.services).map(([sid, price]) => {
                  const s = SERVICE_META[sid];
                  return (
                    <div
                      key={sid}
                      data-testid={`service-card-${sid}`}
                      className={`group relative bg-[#0a0a0a] border ${
                        s.featured ? "border-white/30" : "border-white/10"
                      } p-7 rounded-xl hover:border-white/40 hover:-translate-y-1 transition-all duration-300`}
                    >
                      {s.featured && (
                        <div className="absolute -top-3 left-7 bg-gold text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1">
                          Most popular
                        </div>
                      )}
                      <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                        {s.blurb}
                      </div>
                      <h3 className="font-display text-2xl font-bold mt-2">{s.name}</h3>
                      <div className="flex items-baseline gap-2 mt-5">
                        <span className="font-display text-5xl font-black text-gold">${price}</span>
                        <span className="text-neutral-500 text-sm">NZD</span>
                      </div>
                      <ul className="mt-6 space-y-3">
                        {s.features.map((f) => (
                          <li key={f} className="flex items-start gap-3 text-sm text-neutral-300">
                            <Check className="w-4 h-4 mt-0.5 text-white/70 flex-shrink-0" strokeWidth={2} />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <a
                        href="#booking"
                        data-testid={`select-service-${sid}`}
                        onClick={() => {
                          window.__abPrefill = { vehicle_size: v.id, service_id: sid };
                          window.dispatchEvent(new CustomEvent("ab-prefill"));
                        }}
                        className="mt-7 inline-flex items-center justify-center w-full bg-white/0 border border-white/15 text-white hover:bg-white hover:text-black px-4 py-3 text-xs uppercase tracking-[0.18em] font-bold transition-colors"
                      >
                        Select & Book
                      </a>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        <p className="mt-8 text-xs uppercase tracking-[0.18em] text-neutral-500">
          Heavily soiled vehicles may incur a small surcharge — quoted on arrival.
        </p>
      </div>
    </section>
  );
}
